// Deferred classic script: runs before the standard-actions bundle and the cart
// drawer modules. Adds wait for those so the cart event reaches the drawer.
const cartReady = new Promise((resolve) => {
  if (document.readyState === 'complete') return resolve();
  document.addEventListener('DOMContentLoaded', () => setTimeout(resolve), { once: true });
});

class HyAtcButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.atc-button');
    this.stateEl = this.querySelector('.current-state');
    this.variantLabelEl = this.querySelector('.current-variant');
    this.panel = this.querySelector('.atc-variants');
    this.rows = Array.from(this.querySelectorAll('.atc-variant'));
    this.hasSingleVariant = this.rows.length === 1;

    if (!this.button || !this.stateEl || !this.panel) return;

    this._labels = {
      buy: this.stateEl.dataset.buy || this.stateEl.textContent.trim(),
      select: this.stateEl.dataset.select || 'Select',
      added: this.stateEl.dataset.added || 'Added',
    };

    // a11y + closed-by-default (progressive enhancement, no CSS edits needed)
    // NB: uses inline style rather than the `hidden` attribute — the given
    // CSS sets `.atc-variants { display: flex }` unconditionally, which
    // out-specificities `[hidden] { display: none }`, so `hidden` alone
    // would not actually hide the panel.
    this.button.setAttribute('role', 'button');
    this.button.setAttribute('tabindex', '0');
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('role', 'listbox');
    this.rows.forEach((row) => {
      row.setAttribute('role', 'option');
      row.setAttribute('tabindex', '-1');
      if (row.dataset.available === 'false') {
        row.setAttribute('aria-disabled', 'true');
      }
    });

    this._setState('idle');

    this._onButtonClick = this._onButtonClick.bind(this);
    this._onButtonKeydown = this._onButtonKeydown.bind(this);
    this._onRowClick = this._onRowClick.bind(this);
    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    this.button.addEventListener('click', this._onButtonClick);
    this.button.addEventListener('keydown', this._onButtonKeydown);
    this.rows.forEach((row) => row.addEventListener('click', this._onRowClick));
    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    this.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
  }

  // ---- interaction handlers -----------------------------------------

  _onButtonClick(e) {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Single variant -> add directly
    if (this.hasSingleVariant) {
      if (this._state !== 'idle') return;
      const variant = this.rows[0];
      if (variant.dataset.available === 'false') return;
      this._selectVariant(variant);
      return;
    }

    // Multiple variants -> selector
    if (this._state === 'idle') this._setState('open');
    else if (this._state === 'open') this._setState('idle');
    // ignore clicks while loading/added
  }

  _onButtonKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._onButtonClick(e);
      // Move focus into the listbox so options are reachable by keyboard
      if (this._state === 'open') this.rows[0]?.focus();
    }
  }

  _onKeydown(e) {
    if (e.key === 'Escape' && this._state === 'open') {
      this._setState('idle');
      this.button.focus();
      return;
    }

    const row = e.target.closest('.atc-variant');
    if (!row || this._state !== 'open') return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (row.dataset.available === 'false') return;
      this.button.focus();
      this._selectVariant(row);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = this.rows.indexOf(row) + (e.key === 'ArrowDown' ? 1 : -1);
      this.rows[next]?.focus();
    }
  }

  _onDocPointerDown(e) {
    if (this._state !== 'open') return;
    if (!e.composedPath().includes(this)) this._setState('idle');
  }

  _onRowClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const row = e.currentTarget;
    if (row.dataset.available === 'false') return;
    this._selectVariant(row);
  }

  // ---- state machine ---------------------------------------------------

  _setState(next) {
    const prev = this._state;
    this._state = next;
    this.setAttribute('data-state', next);
    this.button.setAttribute('aria-expanded', String(next === 'open'));

    if (next === 'idle') {
      if(prev === 'open'){
        this.panel.parentElement.classList.add('is-closing-animation');
        const onEnd = (e) => {
          if (e.propertyName !== 'max-height') return;
          this.panel.removeEventListener('transitionend', onEnd);
          this.panel.parentElement.classList.remove('is-closing-animation');
          if (this._state === 'idle') {
            this.stateEl.textContent = this._labels.buy;
            if (this.variantLabelEl) {
              this.variantLabelEl.textContent =
                this.variantLabelEl.dataset.defaultValue;
            }
          }
        };
        this.panel.addEventListener('transitionend', onEnd);
      } else {
        this.stateEl.textContent = this._labels.buy;
        if (this.variantLabelEl) {
          this.variantLabelEl.textContent = this.variantLabelEl.dataset.defaultValue;
        }
      }
    }
    if (next === 'open') this.stateEl.textContent = this._labels.select;
    if (next === 'added') this.stateEl.textContent = this._labels.added;
    // 'loading' intentionally keeps whatever text was last shown; style the
    // loading moment via `hy-atc-button[data-state="loading"]` in CSS
    // (e.g. reduce opacity, show a spinner) rather than swapping text.
  }

  _selectVariant(row) {
    const variantId = row.dataset.variantId;
    const title = row.querySelector('.atc-variant-title')?.textContent.trim() || '';

    this._setState('loading');

    this.dispatchEvent(
      new CustomEvent('hy-atc:add', {
        detail: { variantId, variantEl: row },
        bubbles: true,
        composed: true,
      })
    );

    this._addToCart(variantId)
      .then((cart) => {
        if (this.variantLabelEl) {
          this.variantLabelEl.textContent = title;
        }
        this.rows.forEach((r) => r.classList.toggle('is-current', r === row));

        this.dispatchEvent(
          new CustomEvent('hy-atc:added', {
            detail: { variantId, variantEl: row, cart },
            bubbles: true,
            composed: true,
          })
        );

        this._setState('added');
        this._settleTimer = setTimeout(() => this._setState('idle'), 2100);
      })
      .catch((error) => {
        this.dispatchEvent(
          new CustomEvent('hy-atc:error', {
            detail: { variantId, variantEl: row, error },
            bubbles: true,
            composed: true,
          })
        );
        this._setState('idle');
      });
  }

  // ---- cart integration --------------------------------------------

  /**
   * Default implementation: Shopify's AJAX Cart API.
   * Replace this method (or the fetch below) if you use a different
   * cart backend — it just needs to return a Promise.
   */
  _addToCart(variantId) {
    return cartReady.then(() => this._updateCart(variantId));
  }

  _updateCart(variantId) {
    if (
        window.Shopify &&
        window.Shopify.actions &&
        typeof window.Shopify.actions.updateCart === 'function'
    ) {
        return window.Shopify.actions
        .updateCart(
            {
            lines: [
                {
                merchandiseId: variantId,
                quantity: 1,
                },
            ],
            },
            {
            event: {
                context: 'product',
            },
            }
        )
        .then((result) => {
            if (result?.userErrors?.length) {
            return Promise.reject(result.userErrors);
            }

            return result;
        });
    }

    // Fallback for environments where the theme action API isn't available.
    return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        },
        body: JSON.stringify({
        id: variantId,
        quantity: 1,
        }),
    }).then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
        return Promise.reject(data);
        }

        return data;
    });
    }
  
}

customElements.define('hy-atc-button', HyAtcButton);